import { parseLinks } from "i18n/utils/parseLinks"
import { useTranslate } from "hooks/translate"
import InputField from "app/components/Form/Field/Input"
import SelectField from "app/components/Form/Field/Select"
import CheckboxField from "app/components/Form/Field/Checkbox"
import Heading from "app/components/Heading"
import Description from "app/components/Description"
import Body from "app/components/Form/Body"
import TurnstileField from "app/components/Form/Field/Turnstile"
import { Locales } from "i18n"
import { useSelector } from "react-redux"
import { getAppLocale } from "state/app/selectors"
import { useFormContext } from "react-hook-form"
import { FormStateValues } from "state/form/types"
import { PreOwnedFormStateValues } from "state/preownedForm/types"
import { useAppflow } from "app/hooks/useAppFlow"
import { BankEnum } from "app/pages/appFlow/types"

enum PreferredLanguages {
  NL = "nl",
  FR = "fr",
  DE = "de",
  EN = "en"
}

const legalLinks: {
  [key in Locales]: {
    [key: string]: string
  }
} = {
  [Locales.NL]: {
    link_privacy_policy:
      "https://legal.polestar.com/nl-be/privacy/f2bfd98d7c84abc5c0a801514bc82f50/",
    link_info_notice:
      "https://legal.polestar.com/nl-be/privacy/a14484e62327aa95c0a8015172fdc42b/",
    link_privacy: "https://legal.polestar.com/nl-be/privacy/"
  },
  [Locales.FR]: {
    link_privacy_policy:
      "https://legal.polestar.com/fr-be/privacy/f2bfd98d7c84abc5c0a801514bc82f50/",
    link_info_notice:
      "https://legal.polestar.com/fr-be/privacy/a14484e62327aa95c0a8015172fdc42b/",
    link_privacy: "https://legal.polestar.com/fr-be/privacy/"
  }
}

const Step2: React.FC = () => {
  const { bank, clientType } = useAppflow()
  const { t } = useTranslate()
  const locale = useSelector(getAppLocale)
  const { control } = useFormContext<
    FormStateValues | PreOwnedFormStateValues
  >()

  const preferredLanguageOptions = Object.values(PreferredLanguages)
    .map(locale => ({
      label: t(`system.locale.${locale}`),
      value: locale
    }))
    .filter(v =>
      bank === BankEnum.CBC ? v.value === PreferredLanguages.FR : true
    )

  return (
    <>
      <Body>
        <Heading>{t(`form.step_1.${clientType}.heading`)}</Heading>
        <Description>{t(`form.step_1.${clientType}.body`)}</Description>
      </Body>
      <InputField
        name="firstName"
        label="system.first_name"
        rules={{ required: true }}
        control={control}
      />
      <InputField
        name="lastName"
        label="system.last_name"
        rules={{ required: true }}
        control={control}
      />
      <InputField
        name="zip"
        label="system.zip"
        rules={{ required: true }}
        control={control}
      />
      <InputField
        name="email"
        label="system.email"
        inputMode="email"
        type="email"
        rules={{
          required: true,
          pattern: {
            value: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
            message: t("error.email")
          }
        }}
        control={control}
      />
      <InputField
        name="phone"
        label="system.phone"
        description={t("form.field.phone.description")}
        type="tel"
        rules={{
          required: true,
          pattern: {
            value: /^\+32[^0][0-9]{6,}$/,
            message: t("error.phone")
          }
        }}
        control={control}
      />
      <SelectField
        name="language"
        label="form.field.preferred_language.label"
        options={preferredLanguageOptions}
        rules={{ required: true }}
        control={control}
      />
      <CheckboxField
        name="validQuotationDisclaimer"
        label={t("system.legal.valid_quotation", {
          br: <br />
        })}
        rules={{ required: true }}
        control={control}
      />
      <CheckboxField
        name="informationNotice"
        label={t(
          "form.field.information_notice.label",
          parseLinks({
            link_info_notice: {
              target: "_blank",
              href: legalLinks[locale].link_info_notice
            }
          })
        )}
        rules={{ required: true }}
        control={control}
      />
      <CheckboxField
        name="privacyPolicy"
        label={t(
          "form.field.privacy_policy.label",
          parseLinks({
            link_privacy_policy: {
              target: "_blank",
              href: legalLinks[locale].link_privacy_policy
            },
            link_info_notice: {
              target: "_blank",
              href: legalLinks[locale].link_info_notice
            },
            link_privacy: {
              target: "_blank",
              href: legalLinks[locale].link_privacy
            }
          })
        )}
        control={control}
      />
      {locale === Locales.NL && (
        <TurnstileField
          lang={Locales.NL}
          name="turnstile"
          rules={{ required: true }}
          control={control}
        />
      )}
      {locale === Locales.FR && (
        <TurnstileField
          lang={Locales.FR}
          name="turnstile"
          rules={{ required: true }}
          control={control}
        />
      )}
    </>
  )
}

export default Step2
